/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { AccordionGroup, Grid, PageGridContainer, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { AccordionGroupProps, GridProps } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import DottedSeparator from 'bundles/unified-description-page-common/components/shared/DottedSeparator';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

interface Props {
  title: string | React.ReactNode;
  description?: React.ReactNode;
  titleColumnGridOptions?: GridProps;
  syllabusContentGridOptions?: GridProps;
  sidebarContentGridOptions?: GridProps;
  syllabusItems: AccordionGroupProps['children'];
  sidebar?: React.ReactNode;
  anchorId?: string;
  footer?: React.ReactNode;
  slug?: string;
  totalHours: number;
  modulesCount: number;
}

const styles = {
  title: css`
    margin-bottom: calc(var(--cds-spacing-100) * -1);
  `,
  syllabusContent: css`
    ${xdpStyles.border('border')}
    border-radius: 8px;
    overflow: hidden;
  `,
  // This is a hack to prevent the underline from appearing on the accordion button when it is focused
  // This state class is managed by the CDS accordion component and for an unknwon reason styling specificity rules
  // don't work as intended. Having it here works fine.
  accordionGroup: css`
    padding: 0;
    margin: 0;

    .cds-button-focusVisible {
      text-decoration: none !important;
    }
  `,
  sidebar: css`
    ${xdpStyles.border('border')}
    border-radius: 8px;
    padding: var(--cds-spacing-300);
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
      padding: var(--cds-spacing-200);
    }
  `,
  footer: css`
    &:has(> *) {
      display: flex;
      flex-direction: column;
      gap: var(--cds-spacing-200);
      padding: var(--cds-spacing-200);
    }

    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200);
    }
  `,
  badgeSpacing: css`
    /* Handle LTR and RTL */
    display: inline-block;
    padding-left: var(--cds-spacing-100);
    padding-right: var(--cds-spacing-100);
  `,
  divider: css`
    margin: 0 var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);
    height: 1px;
    background-color: var(--cds-color-grey-50);

    ${breakpoints.down('xs')} {
      margin: 0 0 var(--cds-spacing-200) 0;
    }
  `,
  descriptionGrid: css`
    > * {
      margin-top: 0;
    }

    padding-top: 0 !important;
  `,
};

function SyllabusModuleRedesignV2({
  title,
  description,
  titleColumnGridOptions = {
    xs: 12,
  },
  syllabusContentGridOptions = {
    xs: 12,
    md: 9,
  },
  sidebarContentGridOptions = {
    xs: 12,
    md: 3,
  },
  syllabusItems,
  sidebar,
  anchorId,
  footer,
  slug,
  totalHours,
  modulesCount,
}: Props) {
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const eventingProductData = useEventingProductData();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'syllabus',
      },
      ...eventingProductData,
    }
  );

  const descriptionGridOptions: GridProps = {
    xs: 12,
    md: 6,
  };

  const items = useMemo(() => {
    if (!syllabusItems || !(Array.isArray(syllabusItems) && syllabusItems.length)) {
      return null;
    }
    return syllabusItems.map((item) => item);
  }, [syllabusItems]);

  if (!items) {
    return null;
  }

  return (
    <Section anchorId={anchorId} ref={visibilityTrackingRef} spacing={8}>
      <PageGridContainer>
        <Grid item container spacing={16}>
          <Grid item {...titleColumnGridOptions}>
            <Typography2 variant="titleSmall" component="h2" css={styles.title}>
              {title}
            </Typography2>
          </Grid>
          <Grid item {...descriptionGridOptions} css={styles.descriptionGrid}>
            <Typography2 variant={isMobile ? 'bodyPrimary' : 'bodySecondary'} color="supportText" component="div">
              <Grid container>
                <Grid item>
                  <FormattedMessage
                    message={_t('{modulesCount, plural, =1 {# skill goal} other {# skill goals}}')}
                    modulesCount={modulesCount}
                  />
                  <DottedSeparator />
                </Grid>
                <Grid item>
                  <FormattedMessage
                    message={_t('{totalHours, plural, =1 {# hour} other {# hours}}')}
                    totalHours={totalHours}
                  />
                </Grid>
                <Grid item>
                  <DottedSeparator />
                  {_t('Earn a certificate')}
                </Grid>
              </Grid>
            </Typography2>
          </Grid>
          <Grid item {...syllabusContentGridOptions}>
            <TrackedDiv
              trackingName="syllabus"
              trackClicks={false}
              requireFullyVisible={false}
              withVisibilityTracking={true}
            >
              <div css={styles.syllabusContent}>
                {isMobile ? (
                  <React.Fragment>{items}</React.Fragment>
                ) : (
                  <AccordionGroup css={styles.accordionGroup} multiExpand spacing={8}>
                    {items}
                  </AccordionGroup>
                )}
                {footer && <div css={styles.footer}>{footer}</div>}
              </div>
            </TrackedDiv>
          </Grid>
          <Grid item {...sidebarContentGridOptions}>
            {sidebar && <div css={styles.sidebar}>{sidebar}</div>}
          </Grid>
        </Grid>
      </PageGridContainer>
    </Section>
  );
}

export default SyllabusModuleRedesignV2;
